import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KoobAlert,
  KoobBadge,
  KoobButton,
  KoobInputFile,
  KoobModal,
  KoobLoader,
  KoobTable,
  KoobTableBody,
  KoobTableCell,
  KoobTableHead,
  KoobTableHeader,
  KoobTableRow,
} from '@koob/ui';
import { internalGet as get, internalPost as post } from 'api/node';
import { useError } from 'hooks';

// The organization is the acting one (X-Organization-Id), never a parameter.
const ROOT = '/experience-compatibility-imports';
const PAGE_SIZE = 50;
const PROCESSING_STATUSES = ['analyzing', 'applying', 'uploading'];
// icon-less statuses are in progress and get a spinner
const STATUS_ALERTS = {
  uploading: { variant: 'info' },
  analyzing: { variant: 'info' },
  applying: { variant: 'info' },
  ready: { variant: 'info', icon: 'fa-circle-check' },
  invalid: { variant: 'error', icon: 'fa-circle-exclamation' },
  completed: { variant: 'success', icon: 'fa-circle-check' },
  failed: { variant: 'error', icon: 'fa-circle-xmark' },
};
const SUMMARY_KEYS = [
  { key: 'created', color: 'text-green-600' },
  { key: 'updated', color: 'text-blue-600' },
  { key: 'removed', color: 'text-red-600' },
  { key: 'unchanged', color: 'text-foreground' },
  { key: 'skippedRows', color: 'text-foreground' },
];

// keyed by the API's cell values ('' = no explicit rule)
const VALUE_BADGES = {
  PREFERRED: { color: 'green', icon: 'fa-thumbs-up', label: 'bestMatch' },
  INCOMPATIBLE: { color: 'red', icon: 'fa-ban', label: 'blackListed' },
  '': { color: 'gray', icon: 'fa-minus', label: 'neutral' },
};
const DIRECTION_ICONS = {
  FOR: 'fa-arrow-right text-blue-600',
  TO: 'fa-arrow-left text-purple-600',
};

export default function CompatibilityImportModal({
  open,
  onClose,
  organizationId,
  onImported,
}) {
  const { t } = useTranslation('experiences');
  const { sendErrorSnack } = useError();
  const [file, setFile] = useState(null);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [pollVersion, setPollVersion] = useState(0);
  const completedImports = useRef(new Set());
  const onImportedRef = useRef(onImported);
  useEffect(() => {
    onImportedRef.current = onImported;
  }, [onImported]);
  const storageKey = `compatibility-import:${organizationId || 'current'}`;
  const [importId, setImportId] = useState(() =>
    sessionStorage.getItem(storageKey),
  );

  useEffect(() => {
    if (!open || !importId) return;
    let cancelled = false;
    let timer;
    const refresh = async () => {
      try {
        const { data } = await get(
          `${ROOT}/${importId}?page=${offset / PAGE_SIZE + 1}&perPage=${PAGE_SIZE}`,
        );
        if (cancelled) return;
        setReview(data);
        setError('');
        if (PROCESSING_STATUSES.includes(data.status))
          timer = setTimeout(refresh, 2000);
        if (
          data.status === 'completed' &&
          !completedImports.current.has(importId)
        ) {
          completedImports.current.add(importId);
          onImportedRef.current?.();
        }
      } catch (err) {
        if (!cancelled) {
          setError(t('matrixImport.pollError'));
          timer = setTimeout(refresh, 5000);
        }
      }
    };
    refresh();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, importId, offset, pollVersion, t]);

  const analyze = async () => {
    if (!file) return;
    if (
      !file.name.toLowerCase().endsWith('.xlsx') ||
      file.size > 20 * 1024 * 1024 ||
      !file.size
    ) {
      setError(t('matrixImport.fileError'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await post(ROOT, form);
      setOffset(0);
      setReview(data);
      setImportId(data.id);
      sessionStorage.setItem(storageKey, data.id);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          t('matrixImport.pollError'),
      );
      sendErrorSnack(err);
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await post(`${ROOT}/${importId}/confirmation`, {
        confirmed: true,
        reviewToken: review.reviewToken,
      });
      setReview((previous) => ({ ...previous, ...data }));
      setPollVersion((version) => version + 1);
      setOffset(0);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          t('matrixImport.pollError'),
      );
      sendErrorSnack(err);
    } finally {
      setBusy(false);
    }
  };
  const download = async (kind) => {
    setDownloading(kind);
    try {
      const { data } = await get(
        `${ROOT}/${importId}/${kind}`,
        { responseType: 'blob' },
        false,
      );
      const url = URL.createObjectURL(data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download =
        kind === 'report'
          ? 'compatibility-report.json'
          : 'experience-compatibilities.xlsx';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          t('matrixImport.pollError'),
      );
      sendErrorSnack(err);
    } finally {
      setDownloading(null);
    }
  };
  const reset = () => {
    setImportId(null);
    setReview(null);
    setFile(null);
    setError('');
    setOffset(0);
    sessionStorage.removeItem(storageKey);
  };
  const processing = busy || PROCESSING_STATUSES.includes(review?.status);
  const errors = review?.errorsCount > 0;
  const count = errors ? review.errorsCount : review?.changesCount || 0;
  const status = review && STATUS_ALERTS[review.status];
  const valueBadge = (value) => {
    const badge = VALUE_BADGES[value || ''];
    if (!badge) return value;
    return (
      <KoobBadge color={badge.color}>
        <i className={`fa-regular ${badge.icon}`} aria-hidden />
        {t(`matrixImport.${badge.label}`)}
      </KoobBadge>
    );
  };
  const columns = errors
    ? ['sheet', 'row', 'column', 'value', 'message']
    : [
        'experience',
        'relatedExperience',
        'direction',
        'before',
        'after',
        'reason',
      ];
  const rows = errors
    ? review.errors.map((item) => [
        item.sheet || '—',
        item.row || '—',
        item.column || '—',
        item.value || '—',
        item.message,
      ])
    : (review?.changes || []).map((item) => [
        item.experienceId,
        item.relatedExperienceId,
        <span
          key="direction"
          className="inline-flex items-center gap-1.5 whitespace-nowrap"
        >
          <i
            className={`fa-regular ${DIRECTION_ICONS[item.direction]}`}
            aria-hidden
          />
          {t(`matrixImport.directions.${item.direction}`)}
        </span>,
        valueBadge(item.currentValue),
        valueBadge(item.proposedValue),
        item.reason || '—',
      ]);

  return (
    <KoobModal
      open={open}
      setOpen={onClose}
      title={t('matrixImport.title')}
      widthClass="sm:max-w-5xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('matrixImport.description')}
        </p>

        {!importId && (
          <KoobInputFile
            value={file}
            onValueChange={(value) => {
              setFile(value);
              setError('');
            }}
            accept=".xlsx"
            hint={t('matrixImport.file')}
            error={Boolean(error)}
            disabled={busy}
          />
        )}

        {error && (
          <KoobAlert variant="error" icon="fa-circle-exclamation">
            {error}
          </KoobAlert>
        )}

        {status && (
          <KoobAlert
            variant={status.variant}
            role="status"
            aria-live="polite"
            icon={status.icon || <KoobLoader color="currentColor" size={16} />}
            title={t(`matrixImport.status.${review.status}`)}
            description={review.failure}
          />
        )}

        {review?.summary && (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {SUMMARY_KEYS.map(({ key, color }) => (
              <div
                key={key}
                className="rounded-md border border-solid border-border p-3"
              >
                <dt className="text-xs text-muted-foreground">
                  {t(`matrixImport.${key}`)}
                </dt>
                <dd className={`mt-1 text-2xl font-semibold ${color}`}>
                  {review.summary[key]}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {review?.criteria && (
          <p className="text-sm text-muted-foreground">{review.criteria}</p>
        )}

        {review?.ambiguities?.length > 0 && (
          <KoobAlert
            variant="warning"
            role="note"
            icon="fa-triangle-exclamation"
            title={t('matrixImport.ambiguities')}
          >
            <ul className="list-disc space-y-1 pl-5">
              {review.ambiguities.map((value, i) => (
                <li key={i}>
                  {typeof value === 'string'
                    ? value
                    : `${value.experienceId} → ${value.relatedExperienceId} (${value.direction}) : ${value.reason}`}
                </li>
              ))}
            </ul>
          </KoobAlert>
        )}

        {count > 0 && (
          <div className="max-h-80 overflow-auto rounded-md">
            <KoobTable>
              <KoobTableHeader>
                <KoobTableRow>
                  {columns.map((key) => (
                    <KoobTableHead key={key}>
                      {t(`matrixImport.${key}`)}
                    </KoobTableHead>
                  ))}
                </KoobTableRow>
              </KoobTableHeader>
              <KoobTableBody>
                {rows.map((cells, i) => (
                  <KoobTableRow key={i}>
                    {cells.map((value, j) => (
                      <KoobTableCell key={j}>{value}</KoobTableCell>
                    ))}
                  </KoobTableRow>
                ))}
              </KoobTableBody>
            </KoobTable>
          </div>
        )}

        {count > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 text-sm">
            <KoobButton
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              <i className="fa-regular fa-chevron-left" aria-hidden />
              {t('matrixImport.previous')}
            </KoobButton>
            <span className="text-muted-foreground">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, count)} / {count}
            </span>
            <KoobButton
              size="sm"
              disabled={offset + PAGE_SIZE >= count}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              {t('matrixImport.next')}
              <i className="fa-regular fa-chevron-right" aria-hidden />
            </KoobButton>
          </div>
        )}

        {review?.status === 'ready' && (
          <KoobAlert variant="warning" icon="fa-circle-info">
            {t('matrixImport.confirmDescription')}
          </KoobAlert>
        )}

        <div className="flex flex-col-reverse gap-3 border-t border-solid border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {review && !processing && (
              <>
                <KoobButton
                  size="sm"
                  loading={downloading === 'report'}
                  onClick={() => download('report')}
                >
                  <i className="fa-regular fa-file-lines" aria-hidden />
                  {t('matrixImport.downloadReport')}
                </KoobButton>
                <KoobButton
                  size="sm"
                  loading={downloading === 'workbook'}
                  onClick={() => download('workbook')}
                >
                  <i className="fa-regular fa-file-excel" aria-hidden />
                  {t('matrixImport.downloadWorkbook')}
                </KoobButton>
              </>
            )}
            {importId && ((review && !processing) || (error && !review)) && (
              <KoobButton size="sm" variant="ghost" onClick={reset}>
                <i className="fa-regular fa-arrow-rotate-left" aria-hidden />
                {t('matrixImport.another')}
              </KoobButton>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <KoobButton onClick={onClose}>{t('matrixImport.close')}</KoobButton>
            {!importId && (
              <KoobButton
                variant="primary"
                loading={busy}
                disabled={!file}
                onClick={analyze}
              >
                <i className="fa-regular fa-magnifying-glass" aria-hidden />
                {t('matrixImport.analyze')}
              </KoobButton>
            )}
            {review?.status === 'ready' && (
              <KoobButton variant="primary" loading={busy} onClick={confirm}>
                <i className="fa-regular fa-check" aria-hidden />
                {t('matrixImport.confirm')}
              </KoobButton>
            )}
          </div>
        </div>
      </div>
    </KoobModal>
  );
}
